import { Search, Bell, Home, Users, Plus, ScanLine, UserPlus, Clock, ChevronRight, Phone, MessageCircle, MoreVertical, Star, Check, Mail, Video } from "lucide-react";

/* ----------------------------------------------------------
   MockPhone — a library of fake phone screens for BizBinder.
   NO real customer data. All names/companies invented.
---------------------------------------------------------- */

export function MockPhoneFrame({ children, className = "" }) {
  return (
    <div className={`device-frame ${className}`}>
      <div className="device-screen bg-[#F4F3FB]">
        <div className="w-full h-full flex flex-col relative">
          <StatusBar />
          {children}
        </div>
      </div>
    </div>
  );
}

function StatusBar({ title }) {
  return (
    <div className="flex justify-between items-center px-6 pt-4 text-[10px] text-[color:var(--ink)]/80 font-medium">
      <span>9:41</span>
      <span />
      <span className="flex items-center gap-1">
        <span>●●●</span>
      </span>
    </div>
  );
}

/* ---------- 1. HOME DASHBOARD ---------- */
export function MockHome({ greeting = "Good afternoon,", name = "Marcus" }) {
  const initials = name
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("");
  const groups = [
    { name: "Distributors", count: 24, bg: "#FFE7DC", fg: "#E85A2A", emoji: "📦" },
    { name: "Buyers", count: 41, bg: "#E5EAFF", fg: "#1E3AC4", emoji: "🛒" },
    { name: "Suppliers", count: 18, bg: "#F1E7FF", fg: "#7C3AED", emoji: "🏭" },
    { name: "VIP Clients", count: 7,  bg: "#FFF1D6", fg: "#B77800", emoji: "⭐" },
  ];
  const reminders = [
    { name: "Alex Bennett", tag: "Ship 200 units · Friday",   time: "10:00 AM" },
    { name: "Priya Kapoor", tag: "Send tile quote v2",        time: "01:30 PM" },
  ];
  return (
    <div className="flex-1 flex flex-col px-5 pt-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] text-[color:var(--ink-mute)]">{greeting}</div>
          <div className="text-[color:var(--ink)] text-lg font-display font-medium leading-tight">
            {name} <span className="text-[color:var(--accent)]">👋</span>
          </div>
        </div>
        <div className="w-9 h-9 rounded-full bg-[color:var(--blue)] text-white text-xs flex items-center justify-center font-medium">{initials}</div>
      </div>

      <div className="mt-3 flex items-center gap-2 h-9 rounded-full bg-white border border-[color:var(--line)] px-3">
        <Search className="w-3.5 h-3.5 text-[color:var(--ink-mute)]" />
        <span className="text-[10px] text-[color:var(--ink-mute)]">Search contacts, groups, notes…</span>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-[11px] font-medium text-[color:var(--ink)]">My Groups</div>
        <div className="text-[10px] text-[color:var(--blue)]">View all</div>
      </div>
      <div className="mt-2 grid grid-cols-4 gap-2">
        {groups.map((g) => (
          <div key={g.name} className="rounded-xl bg-white border border-[color:var(--line)] p-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-sm" style={{ background: g.bg }}>{g.emoji}</div>
            <div className="mt-1.5 text-[8.5px] font-medium text-[color:var(--ink)] leading-tight truncate">{g.name}</div>
            <div className="text-[8px]" style={{ color: g.fg }}>{g.count}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-[11px] font-medium text-[color:var(--ink)]">Upcoming reminders</div>
        <div className="text-[10px] text-[color:var(--blue)]">See all</div>
      </div>
      <div className="mt-2 space-y-1.5">
        {reminders.map((r, i) => (
          <div key={i} className="rounded-xl bg-white border border-[color:var(--line)] px-2.5 py-2 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-medium text-white shrink-0"
                 style={{ background: i === 0 ? "#E85A2A" : "#1E3AC4" }}>
              {r.name.split(" ").map((s) => s[0]).join("")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[9.5px] font-medium text-[color:var(--ink)] truncate">{r.name}</div>
              <div className="text-[8px] text-[color:var(--ink-mute)] truncate">{r.tag}</div>
            </div>
            <div className="text-[8px] text-[color:var(--ink)]/70">{r.time}</div>
          </div>
        ))}
      </div>

      <div className="mt-auto pb-3">
        <BottomNav active="home" />
      </div>
    </div>
  );
}

/* ---------- 2. GROUP DETAIL ---------- */
export function MockGroup() {
  const contacts = [
    { name: "Alex Bennett",  sub: "Kingdom Trading Co.",  color: "#1E3AC4" },
    { name: "Priya Kapoor",  sub: "Verdant Ceramics",     color: "#E85A2A" },
    { name: "Marcus Chen",   sub: "Riverbend Wholesale",  color: "#7C3AED" },
    { name: "Sofia Rossi",   sub: "Nord Distributors",    color: "#0F766E" },
    { name: "Diego Alvarez", sub: "Solmar Supplies",      color: "#B45309" },
  ];
  return (
    <div className="flex-1 flex flex-col px-4 pt-3">
      <div className="flex items-center justify-between">
        <div className="w-6 h-6 rounded-full bg-white border border-[color:var(--line)] flex items-center justify-center">
          <ChevronRight className="w-3 h-3 rotate-180 text-[color:var(--ink)]" />
        </div>
        <div className="text-[11px] font-medium text-[color:var(--ink)]">Distributors</div>
        <MoreVertical className="w-3.5 h-3.5 text-[color:var(--ink)]" />
      </div>

      <div className="mt-3 text-[9px] text-[color:var(--ink-mute)] uppercase tracking-widest">24 contacts · 3 sub-groups</div>

      <div className="mt-2 flex gap-1.5">
        {["All", "Wholesale", "Ceramics", "Consumables"].map((t, i) => (
          <div key={t} className={`px-2 py-0.5 rounded-full text-[9px] ${i === 0 ? "bg-[color:var(--ink)] text-white" : "bg-white border border-[color:var(--line)] text-[color:var(--ink)]/70"}`}>{t}</div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2 h-8 rounded-full bg-white border border-[color:var(--line)] px-3">
        <Search className="w-3 h-3 text-[color:var(--ink-mute)]" />
        <span className="text-[9px] text-[color:var(--ink-mute)]">Search in group…</span>
      </div>

      <div className="mt-3 space-y-1.5 flex-1 overflow-hidden">
        {contacts.map((c) => (
          <div key={c.name} className="rounded-xl bg-white border border-[color:var(--line)] px-2.5 py-2 flex items-center gap-2">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-[8px] font-medium text-white shrink-0" style={{ background: c.color }}>
              {c.name.split(" ").map((s) => s[0]).join("")}
            </div>
            <div className="flex-1 min-w-0 pr-1">
              <div className="text-[9.5px] font-medium text-[color:var(--ink)] truncate leading-tight">{c.name}</div>
              <div className="text-[8px] text-[color:var(--ink-mute)] truncate leading-tight mt-0.5">{c.sub}</div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <div className="w-5 h-5 rounded-full bg-[color:var(--blue-soft)] flex items-center justify-center">
                <Phone className="w-2 h-2 text-[color:var(--blue)]" />
              </div>
              <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                <MessageCircle className="w-2 h-2 text-emerald-600" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-2 pb-3">
        <BottomNav active="contacts" />
      </div>
    </div>
  );
}

/* ---------- 3. QUICK ACTIONS BOTTOM SHEET ---------- */
export function MockActions() {
  const actions = [
    { icon: ScanLine, label: "Scan Business Card", sub: "AI extracts the details",       color: "#E85A2A", bg: "#FFE7DC" },
    { icon: UserPlus, label: "Add New Contact",    sub: "Manually, with full details",   color: "#1E3AC4", bg: "#E5EAFF" },
    { icon: Bell,     label: "Set a Reminder",     sub: "For any contact or task",       color: "#7C3AED", bg: "#F1E7FF" },
  ];
  return (
    <div className="flex-1 flex flex-col relative overflow-hidden">
      {/* Faded background home */}
      <div className="opacity-30 pointer-events-none absolute inset-0">
        <MockHome />
      </div>
      <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" />

      <div className="mt-auto relative rounded-t-3xl bg-white border-t border-x border-[color:var(--line)] px-4 py-4 pb-5 shadow-2xl">
        <div className="mx-auto w-10 h-1 rounded-full bg-black/15 mb-3" />
        <div className="text-[11px] font-medium text-[color:var(--ink)]">Quick add</div>
        <div className="mt-3 space-y-2">
          {actions.map((a) => (
            <div key={a.label} className="rounded-xl border border-[color:var(--line)] px-3 py-2.5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: a.bg }}>
                <a.icon className="w-4 h-4" style={{ color: a.color }} strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-medium text-[color:var(--ink)]">{a.label}</div>
                <div className="text-[8.5px] text-[color:var(--ink-mute)]">{a.sub}</div>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-[color:var(--ink-mute)]" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- 4. FOLLOW-UP DASHBOARD ---------- */
export function MockFollowups() {
  const items = [
    { name: "Alex Bennett",  note: "Ship 200 units",         time: "10:00 AM", tag: "Today",    color: "#E85A2A" },
    { name: "Priya Kapoor",  note: "Send tile quote v2",     time: "01:30 PM", tag: "Today",    color: "#1E3AC4" },
    { name: "Sofia Rossi",   note: "Confirm delivery slot",  time: "Tomorrow", tag: "Upcoming", color: "#7C3AED" },
    { name: "Diego Alvarez", note: "Renew retainer",         time: "2d ago",   tag: "Overdue",  color: "#0F766E" },
  ];
  return (
    <div className="flex-1 flex flex-col px-3 pt-3">
      <div className="text-[8.5px] text-[color:var(--ink-mute)] uppercase tracking-widest">Follow-ups</div>
      <div className="mt-0.5 font-display text-xl font-medium text-[color:var(--ink)] leading-none">08</div>

      <div className="mt-2.5 flex gap-1 p-0.5 bg-white border border-[color:var(--line)] rounded-full text-[8px]">
        {["Today", "Upcoming", "Overdue", "Done"].map((t, i) => (
          <div key={t} className={`flex-1 text-center py-1 rounded-full ${i === 0 ? "bg-[color:var(--ink)] text-white" : "text-[color:var(--ink)]/70"}`}>{t}</div>
        ))}
      </div>

      <div className="mt-2 space-y-1.5 flex-1 overflow-hidden">
        {items.map((r, i) => (
          <div key={i} className="rounded-xl bg-white border border-[color:var(--line)] px-2 py-1.5">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[7.5px] font-medium text-white shrink-0" style={{ background: r.color }}>
                {r.name.split(" ").map((s) => s[0]).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] font-semibold text-[color:var(--ink)] truncate leading-tight">{r.name}</div>
                <div className="text-[7.5px] text-[color:var(--ink-mute)] truncate leading-tight mt-0.5">{r.note}</div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-[7.5px] font-medium text-[color:var(--ink)] leading-none">{r.time}</div>
                <div
                  className="mt-1 text-[6.5px] font-semibold uppercase tracking-widest"
                  style={{ color: r.tag === "Overdue" ? "#B91C1C" : r.tag === "Today" ? "var(--accent)" : "var(--ink-mute)" }}
                >
                  {r.tag}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-2 pb-3">
        <BottomNav active="reminders" />
      </div>
    </div>
  );
}

/* ---------- 5. GLOBAL SEARCH ---------- */
export function MockSearch() {
  const results = [
    { name: "Sofia Rossi",   sub: "Nord Distributors · Milan",  match: "tile quote" },
    { name: "Alex Bennett",  sub: "Kingdom Trading Co.",        match: "ceramic tile" },
    { name: "Yusuf Rahman",  sub: "Luxor Interiors",            match: "tile order" },
  ];
  return (
    <div className="flex-1 flex flex-col px-3 pt-3">
      <div className="flex items-center gap-1.5 h-8 rounded-full bg-white border border-[color:var(--blue)]/40 px-2.5">
        <Search className="w-3 h-3 text-[color:var(--blue)] shrink-0" />
        <span className="text-[9px] text-[color:var(--ink)] truncate">tile quote</span>
        <span className="ml-auto w-[1px] h-3 bg-[color:var(--ink)] animate-pulse shrink-0" />
      </div>

      <div className="mt-2 text-[7px] uppercase tracking-widest text-[color:var(--ink-mute)] whitespace-nowrap">
        3 matches · name, company, notes
      </div>

      <div className="mt-2 space-y-1.5 flex-1 overflow-hidden">
        {results.map((r, i) => (
          <div key={i} className="rounded-xl bg-white border border-[color:var(--line)] px-2 py-1.5">
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[7.5px] font-medium text-white shrink-0"
                   style={{ background: ["#1E3AC4", "#E85A2A", "#7C3AED"][i] }}>
                {r.name.split(" ").map((s) => s[0]).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[8.5px] font-semibold text-[color:var(--ink)] truncate leading-tight">{r.name}</div>
                <div className="text-[7px] text-[color:var(--ink-mute)] truncate leading-tight">{r.sub}</div>
              </div>
            </div>
            {i === 0 && (
              <div className="mt-1 text-[7.5px] text-[color:var(--ink)]/75 leading-snug line-clamp-2">
                Note: “Sent <span className="bg-yellow-100 px-0.5 rounded">tile quote</span> v2 last Tuesday.”
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-auto pb-3">
        <BottomNav active="contacts" />
      </div>
    </div>
  );
}

/* ---------- 6. CARD SCANNER ---------- */
export function MockScanner() {
  return (
    <div className="flex-1 flex flex-col px-3 pt-3">
      <div className="flex items-center justify-between">
        <div className="w-6 h-6 rounded-full bg-white border border-[color:var(--line)] flex items-center justify-center">
          <ChevronRight className="w-3 h-3 rotate-180 text-[color:var(--ink)]" />
        </div>
        <div className="text-[10px] font-medium text-[color:var(--ink)]">Scan a card</div>
        <div className="w-6" />
      </div>

      {/* Card preview — smaller aspect + tighter type */}
      <div className="mt-3 rounded-lg overflow-hidden relative bg-gradient-to-br from-[#0A0A1F] to-[#1E3AC4] shadow-lg" style={{ aspectRatio: "1.7 / 1" }}>
        <div className="absolute inset-0 p-2.5 flex flex-col justify-between">
          <div>
            <div className="text-[6px] uppercase tracking-[0.2em] text-white/60 font-medium">Sales Director</div>
            <div className="mt-0.5 text-white text-[11px] font-semibold leading-tight">Hannah Weiss</div>
            <div className="text-[7px] text-white/70 mt-0.5">Nord Distributors</div>
          </div>
          <div className="text-[6px] text-white/70 leading-snug">
            <div>+49 30 5555 0142</div>
            <div>hannah@nord.co</div>
          </div>
        </div>
        {/* Scanner overlay */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-x-2 h-0.5 bg-[color:var(--accent)] shadow-[0_0_10px_2px_#E85A2A] animate-[scan_2.4s_ease-in-out_infinite]" style={{ top: "20%" }} />
          <div className="absolute top-1.5 left-1.5 w-2.5 h-2.5 border-l-2 border-t-2 border-[color:var(--accent)] rounded-tl" />
          <div className="absolute top-1.5 right-1.5 w-2.5 h-2.5 border-r-2 border-t-2 border-[color:var(--accent)] rounded-tr" />
          <div className="absolute bottom-1.5 left-1.5 w-2.5 h-2.5 border-l-2 border-b-2 border-[color:var(--accent)] rounded-bl" />
          <div className="absolute bottom-1.5 right-1.5 w-2.5 h-2.5 border-r-2 border-b-2 border-[color:var(--accent)] rounded-br" />
        </div>
      </div>

      {/* Extracted fields — stacked (not inline) to avoid overflow */}
      <div className="mt-2.5 rounded-xl bg-white border border-[color:var(--line)] overflow-hidden">
        {[
          { label: "Name",    value: "Hannah Weiss" },
          { label: "Company", value: "Nord Distributors" },
          { label: "Phone",   value: "+49 30 5555 0142" },
          { label: "Email",   value: "hannah@nord.co" },
        ].map((f, i) => (
          <div key={f.label} className={`px-2.5 py-1.5 flex items-center justify-between gap-2 ${i < 3 ? "border-b border-[color:var(--line)]" : ""}`}>
            <div className="min-w-0 flex-1">
              <div className="text-[6.5px] text-[color:var(--ink-mute)] uppercase tracking-[0.18em] leading-none">{f.label}</div>
              <div className="text-[8.5px] text-[color:var(--ink)] font-medium truncate mt-0.5">{f.value}</div>
            </div>
            <Check className="w-2.5 h-2.5 text-emerald-500 shrink-0" strokeWidth={3} />
          </div>
        ))}
      </div>

      <div className="mt-2 grid grid-cols-2 gap-1.5">
        <div className="h-7 rounded-full border border-[color:var(--line)] flex items-center justify-center text-[8.5px] text-[color:var(--ink)]">Retake</div>
        <div className="h-7 rounded-full bg-[color:var(--accent)] flex items-center justify-center text-[8.5px] text-white font-medium">Save contact</div>
      </div>

      <div className="mt-auto pb-3">
        <BottomNav active="contacts" />
      </div>
    </div>
  );
}

/* ---------- 7. CONTACT DETAIL (Call + WhatsApp actions) ---------- */
export function MockContact() {
  return (
    <div className="flex-1 flex flex-col relative">
      <div
        className="absolute top-0 left-0 right-0 h-32 pointer-events-none"
        style={{ background: "linear-gradient(180deg, #E5EAFF 0%, #F1E7FF 60%, transparent 100%)" }}
      />

      <div className="relative flex items-center justify-between px-4 pt-3">
        <div className="w-7 h-7 rounded-full bg-white/70 backdrop-blur border border-[color:var(--line)] flex items-center justify-center">
          <ChevronRight className="w-3 h-3 rotate-180 text-[color:var(--ink)]" />
        </div>
        <div className="w-7 h-7 rounded-full bg-white/70 backdrop-blur border border-[color:var(--line)] flex items-center justify-center">
          <MoreVertical className="w-3 h-3 text-[color:var(--ink)]" />
        </div>
      </div>

      <div className="relative mt-3 flex flex-col items-center px-4">
        <div className="relative">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#2748E8] to-[#1E3AC4] text-white flex items-center justify-center text-base font-semibold shadow-lg shadow-[#1E3AC4]/40 border-2 border-white">AB</div>
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white" />
        </div>
        <div className="mt-2.5 font-display text-[15px] font-semibold text-[color:var(--ink)] tracking-tight">Alex Bennett</div>
        <div className="text-[9px] text-[color:var(--ink-mute)] mt-0.5">CEO · Kingdom Trading Co.</div>
        <div className="mt-2 flex flex-wrap justify-center gap-1">
          <span className="chip !text-[8px] !py-0.5 !px-2 bg-white">Distributors</span>
          <span className="chip !text-[8px] !py-0.5 !px-2" style={{ background: "var(--accent-soft)", color: "var(--accent)", borderColor: "transparent" }}>VIP</span>
        </div>
      </div>

      {/* Primary action row */}
      <div className="mt-4 px-3 grid grid-cols-4 gap-1.5">
        {[
          {
            label: "Call",
            solid: true, bg: "#1E3AC4",
            icon: (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
              </svg>
            ),
          },
          {
            label: "WhatsApp",
            solid: true, bg: "#25D366",
            icon: (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            ),
          },
          {
            label: "Email",
            solid: false, bg: "#7C3AED",
            icon: <Mail className="w-4 h-4" style={{ color: "#7C3AED" }} strokeWidth={2} />,
          },
          {
            label: "Video",
            solid: false, bg: "#E85A2A",
            icon: <Video className="w-4 h-4" style={{ color: "#E85A2A" }} strokeWidth={2} />,
          },
        ].map((a) => (
          <div key={a.label} className="flex flex-col items-center gap-1">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{
                background: a.solid ? a.bg : "white",
                border: a.solid ? "none" : "1px solid var(--line)",
                boxShadow: a.solid ? `0 6px 14px -6px ${a.bg}80` : "0 2px 6px rgba(10,10,31,0.06)",
              }}
            >
              {a.icon}
            </div>
            <div className="text-[7.5px] font-medium text-[color:var(--ink)] text-center leading-tight">{a.label}</div>
          </div>
        ))}
      </div>

      {/* Contact fields — iOS grouped list */}
      <div className="mt-4 mx-3 rounded-2xl bg-white border border-[color:var(--line)] overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-[color:var(--line)]">
          <Phone className="w-3 h-3 text-[color:var(--blue)]" />
          <div className="flex-1">
            <div className="text-[7.5px] text-[color:var(--ink-mute)] uppercase tracking-widest">Mobile</div>
            <div className="text-[9.5px] text-[color:var(--ink)] font-medium">+1 415 555 0142</div>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-2">
          <Mail className="w-3 h-3 text-[color:var(--purple)]" />
          <div className="flex-1">
            <div className="text-[7.5px] text-[color:var(--ink-mute)] uppercase tracking-widest">Email</div>
            <div className="text-[9.5px] text-[color:var(--ink)] font-medium">alex@kingdom.co</div>
          </div>
        </div>
      </div>

      <div className="mt-3 mx-3 rounded-2xl bg-white border border-[color:var(--line)] px-3 py-2 flex items-center gap-2">
        <div className="w-6 h-6 rounded-lg bg-[color:var(--blue-soft)] flex items-center justify-center">
          <Phone className="w-2.5 h-2.5 text-[color:var(--blue)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] font-medium text-[color:var(--ink)] leading-tight">Last call · 4m 12s</div>
          <div className="text-[8px] text-[color:var(--ink-mute)] mt-0.5">Note added via voice</div>
        </div>
        <div className="text-[8px] text-[color:var(--ink-mute)]">Yesterday</div>
      </div>

      <div className="mt-auto pb-3 px-3">
        <BottomNav active="contacts" />
      </div>
    </div>
  );
}

/* ---------- Bottom nav (shared) ---------- */
function BottomNav({ active }) {
  const items = [
    { id: "home",      icon: Home,   label: "Home" },
    { id: "contacts",  icon: Users,  label: "Contacts" },
    { id: "plus",      icon: Plus,   label: "" },
    { id: "reminders", icon: Bell,   label: "Reminders" },
    { id: "groups",    icon: Star,   label: "Groups" },
  ];
  return (
    <div className="rounded-full bg-white border border-[color:var(--line)] px-3 py-1.5 flex items-center justify-between shadow-sm">
      {items.map((it) => {
        const activeState = it.id === active;
        if (it.id === "plus") {
          return (
            <div key={it.id} className="w-8 h-8 rounded-full bg-[color:var(--blue)] flex items-center justify-center text-white -my-2">
              <Plus className="w-4 h-4" strokeWidth={2.5} />
            </div>
          );
        }
        return (
          <div key={it.id} className="flex flex-col items-center gap-0.5">
            <it.icon
              className="w-3.5 h-3.5"
              style={{ color: activeState ? "var(--ink)" : "rgba(10,10,31,0.4)" }}
              strokeWidth={activeState ? 2.3 : 1.7}
            />
            <span className="text-[6.5px] font-medium" style={{ color: activeState ? "var(--ink)" : "rgba(10,10,31,0.4)" }}>{it.label}</span>
          </div>
        );
      })}
    </div>
  );
}
